import { applyTurn, dx, dy, type Direction } from '../../sim/direction';
import {
  footprint,
  pivotTiles,
  wingTiles,
  type CutWidth,
  type Pos,
  type Side,
} from '../../sim/footprint';
import type { Grid } from '../../sim/grid';
import { ACTIONS, type Action, type MotionModel } from './motionModel';

/** The mower's physical shape and strength, as the solver needs it. */
export interface MowerBody {
  /** Decides whether brush blocks. */
  cutPower: number;
  cutWidth: CutWidth;
  /** Which side a wide mower's second track is on (fixed for a run). */
  side: Side;
  /** Floating side reels: also mow the grass either side (see MowerSpec.wings). */
  wings?: boolean;
  /**
   * Smashes through breakable items up to this tier as if they were not there (the gold time's
   * routes, lm-late-balance). Absent: every breakable item blocks, as for par.
   */
  breakPower?: number;
}

export const NARROW_BODY = (cutPower: number): MowerBody => ({
  cutPower,
  cutWidth: 1,
  side: 'left',
});

/**
 * Every (anchor tile, heading, rule state) a mower can occupy at a tile center on a static map,
 * with edges for each legal action whose pivot and destination are clear for the whole footprint.
 */
export class StateSpace {
  readonly size: number;
  private readonly w: number;
  private readonly r: number;

  constructor(
    readonly grid: Grid,
    readonly model: MotionModel,
    readonly body: MowerBody,
  ) {
    // A wide mower that reverses flips its relative side, which this fixed-side space can't
    // represent. No such mower exists (the only wide mower, the tractor, can't reverse).
    if (body.cutWidth > 1 && model.next(model.ruleStates - 1, 'reverse') >= 0) {
      throw new Error('StateSpace: wide mowers that can reverse are not supported');
    }
    this.w = grid.width;
    this.r = model.ruleStates;
    this.size = grid.width * grid.height * 4 * this.r;
  }

  index(x: number, y: number, dir: Direction, r: number): number {
    return ((y * this.w + x) * 4 + dir) * this.r + r;
  }

  decode(i: number): { x: number; y: number; dir: Direction; r: number } {
    const r = i % this.r;
    const rest = (i - r) / this.r;
    const dir = (rest % 4) as Direction;
    const tile = (rest - dir) / 4;
    return { x: tile % this.w, y: Math.floor(tile / this.w), dir, r };
  }

  /** Tiles covered by the mower anchored at (x, y) heading `dir`. */
  footprint(x: number, y: number, dir: Direction): Pos[] {
    return footprint(x, y, dir, this.body.cutWidth, this.body.side);
  }

  /** Tiles mowed there: the footprint, plus either side for floating side reels. */
  mowedAt(x: number, y: number, dir: Direction): Pos[] {
    const tiles = this.footprint(x, y, dir);
    return this.body.wings ? [...tiles, ...wingTiles(x, y, dir)] : tiles;
  }

  /** Plans stay on the lawn: a secret yard beyond it (and its gate) is never needed. */
  private clear(tiles: readonly Pos[]): boolean {
    return tiles.every(
      (p) =>
        this.grid.inBounds(p.x, p.y) &&
        !this.grid.isBlockedFor(p.x, p.y, this.body.cutPower, this.body.breakPower ?? -1),
    );
  }

  /** Whether any tile of the footprint is within a sprinkler's reach (no turning, to be safe). */
  private inSpray(x: number, y: number, dir: Direction): boolean {
    return this.footprint(x, y, dir).some((p) => this.grid.inSprinklerReach(p.x, p.y));
  }

  /** Whether the mower can stand anchored at (x, y) heading `dir`. */
  fits(x: number, y: number, dir: Direction): boolean {
    return this.clear(this.footprint(x, y, dir));
  }

  /** Calls `visit` for each successor of state `i`. */
  forEachSuccessor(i: number, visit: (next: number, action: Action) => void): void {
    const { x, y, dir, r } = this.decode(i);
    const { cutWidth, side } = this.body;
    for (const action of ACTIONS) {
      const r2 = this.model.next(r, action);
      if (r2 < 0) continue;
      const d2 = action === 'straight' ? dir : applyTurn(dir, action);
      if (action !== 'straight' && !this.clear(pivotTiles(x, y, dir, d2, cutWidth, side))) continue;
      // Sprinkler spray makes tiles slippery on a timer; plan as if it were always on there.
      if (action !== 'straight' && this.inSpray(x, y, dir)) continue;
      const x2 = x + dx(d2);
      const y2 = y + dy(d2);
      if (!this.fits(x2, y2, d2)) continue;
      visit(this.index(x2, y2, d2, r2), action);
    }
  }

  /**
   * States from which the mower can keep moving forever without crashing (greatest fixed point:
   * repeatedly discard states whose every successor is discarded). Anything else is a trap.
   */
  computeSafe(): Uint8Array {
    const n = this.size;
    const safe = new Uint8Array(n);
    const outDegree = new Int32Array(n);
    const preds: number[][] = Array.from({ length: n }, () => []);
    for (let i = 0; i < n; i++) {
      const { x, y, dir } = this.decode(i);
      if (!this.fits(x, y, dir)) continue;
      safe[i] = 1;
      this.forEachSuccessor(i, (j) => {
        outDegree[i] = (outDegree[i] as number) + 1;
        (preds[j] as number[]).push(i);
      });
    }
    const queue: number[] = [];
    for (let i = 0; i < n; i++) if (safe[i] && outDegree[i] === 0) queue.push(i);
    while (queue.length > 0) {
      const j = queue.pop() as number;
      if (!safe[j]) continue;
      safe[j] = 0;
      for (const p of preds[j] as number[]) {
        if (!safe[p]) continue;
        outDegree[p] = (outDegree[p] as number) - 1;
        if (outDegree[p] === 0) queue.push(p);
      }
    }
    return safe;
  }
}
